import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Divider,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  DirectionsCar as CarIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    document.title = "Privacy Policy - Adilesson";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Review the privacy policy for Adilesson. Learn how we handle student scheduling and automated WhatsApp reminder data.');
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f4f6f9' }}>
      {/* Navigation Header */}
      <AppBar
        position="sticky"
        sx={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
          color: '#1a237e',
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
          <Box display="flex" alignItems="center" gap={1} onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
            <CarIcon sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: '800', color: '#1a237e' }}>
              drv<span style={{ color: '#1976d2' }}>man</span>
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Back to Home
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 6, flexGrow: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 4,
            border: '1px solid #e0e0e0',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.02)',
          }}
        >
          <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
            <SecurityIcon color="primary" sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold" color="primary">
                Privacy Policy
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last Updated: June 14, 2026
              </Typography>
            </Box>
          </Box>
          
          <Divider sx={{ my: 3 }} />

          <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            At <strong>adilessons</strong>, we are committed to protecting your privacy. This Privacy Policy describes how we collect, use, and share information about you when you use our website, application, scheduling services, and automated notification systems.
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            1. Information We Collect
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            We collect information that you provide directly to us when registering an account, booking lessons, or configuring settings:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3, color: 'text.secondary', fontSize: '0.875rem' }}>
            <li><strong>Account Details:</strong> Name, email address, password, role designation (Instructor, Student, or Admin).</li>
            <li><strong>Contact Info:</strong> Phone numbers (specifically required for automated WhatsApp message delivery via WhapiCell).</li>
            <li><strong>Scheduling Data:</strong> Available calendar slots, lesson bookings, session durations, meeting locations, and hourly pricing details.</li>
            <li><strong>Progress Reports:</strong> Driving skill level evaluations across standard DVSA driving components.</li>
            <li><strong>Billing Data:</strong> Transaction states and subscription tokens. All direct financial transactions are handled securely via PayPal; we do not store credit card credentials on our servers.</li>
          </Box>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            2. How We Use Your Information
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            We utilize the collected information to operate, maintain, and improve our services:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3, color: 'text.secondary', fontSize: '0.875rem' }}>
            <li>Facilitate calendar bookings and student-instructor scheduling.</li>
            <li>Deliver automated lesson confirmations, updates, and 1-day reminders to students via WhatsApp.</li>
            <li>Allow instructors to record, track, and share driving progress ratings.</li>
            <li>Process payments and manage active premium subscriptions.</li>
            <li>Provide technical customer support and respond to support tickets.</li>
          </Box>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            3. Sharing Your Data & WhatsApp Dispatches
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            We do not sell your personal data. Your information is shared only under the following conditions:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3, color: 'text.secondary', fontSize: '0.875rem' }}>
            <li><strong>With Your Instructor/Student:</strong> To coordinate bookings, locations, and progress ratings.</li>
            <li><strong>WhatsApp Delivery APIs:</strong> We dispatch student phone numbers and lesson reminder text payload securely to WhapiCell to send the automated WhatsApp notifications.</li>
            <li><strong>Legal Obligations:</strong> If required by law, regulation, or court order.</li>
          </Box>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            4. Security and Storage
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            We implement industry-standard administrative, physical, and electronic security measures designed to protect your information from unauthorized access, loss, or alteration. However, please remember that no method of transmission over the Internet or method of electronic storage is 100% secure.
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            5. Your Rights and Choices
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            Depending on your location, you may have rights under local laws (e.g. GDPR in the UK/EU) regarding your personal data. This includes the right to access, correct, delete, or limit the processing of personal data stored on our servers. You may contact us at support@adilessons.com to initiate these requests.
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            6. Contact Us
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            If you have any questions or feedback regarding this Privacy Policy, please email us directly at <strong>support@adilessons.com</strong>.
          </Typography>
        </Paper>
      </Container>

      {/* Footer */}
      <Box sx={{ py: 3, bgcolor: '#0d132a', color: '#90a4ae', textAlign: 'center', borderTop: '1px solid #1c2a4a' }}>
        <Typography variant="caption">
          © {new Date().getFullYear()} adilessons. All rights reserved. Designed in the UK.
        </Typography>
      </Box>
    </Box>
  );
};

export default PrivacyPage;
